import { CARS } from './catalog.js';
import { CAR_FRONT } from './car-metrics.js';
import { STARTING_GRID } from './track.js';
import { FLOW_CIRCUIT as circuit, ease } from './circuits.js';
import { planBattles } from './flow-stories.js';
import { battleProgress, positionBattle } from './racecraft.js';

const clamp = value => Math.max(0, Math.min(1, value));
const plans = new WeakMap();
const gap = 8;
const passWidth = 3.9;
const keys = [
  [0, 0, 0], [4, 65, 32], [8.1, 292, 64], [11, 420, 40],
  [14.5, 420 + (circuit.curveEnd - 420) * .5, 40], [18, circuit.curveEnd, 44],
  [23, circuit.curveEnd + 266, 60], [40, circuit.length, 68],
];

function progress(time) {
  let i = 1;
  while (i < keys.length - 1 && time > keys[i][0]) i++;
  const [start, distance, speed] = keys[i - 1], [end, endDistance, endSpeed] = keys[i];
  const span = end - start, u = (time - start) / span, travel = endDistance - distance;
  const a = 3 * travel - span * (2 * speed + endSpeed), b = -2 * travel + span * (speed + endSpeed);
  return distance + speed * span * u + a * u * u + b * u ** 3;
}

function beforeFinish(round, plan, time) {
  const base = progress(time);
  const revised = round.raceVersion >= 6;
  const racingLine = distance => circuit.racingLine(distance, round.raceVersion);
  const cars = CARS.map(({ id }) => {
    const rank = round.gridOrder.indexOf(id), slot = STARTING_GRID[rank];
    const distance = base + slot.distance + (-rank * gap - slot.distance) * ease(time / 3.8)
      - CAR_FRONT[id] * ease(time / 5.5);
    const line = racingLine(distance);
    return { id, distance, lane: slot.lane + (line - slot.lane) * ease((time - 3.8) / 1.7), maneuver: 'follow' };
  });
  for (const action of plan.actions) {
    if (time < action.start) break;
    const u = clamp((time - action.start) / (action.end - action.start));
    const attacker = cars.find(c => c.id === action.attacker), defender = cars.find(c => c.id === action.defender);
    const change = gap * (revised ? battleProgress(action, u) : ease((u - .24) / .52));
    attacker.distance += change; defender.distance -= change;
    if (u < 1) {
      if (revised) { positionBattle(attacker, defender, action, u, racingLine); continue; }
      attacker.lane = racingLine(attacker.distance) + action.side * passWidth * (ease(u / .24) - ease((u - .76) / .24));
      attacker.maneuver = u < .24 ? 'pull-out' : u < .76 ? 'alongside' : 'return';
      defender.maneuver = 'defend';
    }
  }
  // A finished maneuver releases its lane; it does not assign a permanent slot.
  for (const car of cars) if (time > 5.5 && (car.maneuver === 'follow' || (!revised && car.maneuver === 'defend'))) car.lane = racingLine(car.distance);
  const story = plan.story;
  if (time >= 35 && story.finalDuel) {
    const u = clamp((time - 37) / 3), close = u * u * (2 - u);
    const side = round.scenarioIndex % 2 ? 1 : -1;
    const attacker = cars.find(c => c.id === round.winnerId);
    const defender = cars.find(c => c.id === plan.closingOrder[0]);
    attacker.distance = base - CAR_FRONT[attacker.id] - gap + gap * close;
    defender.distance = base - CAR_FRONT[defender.id] - story.gap * close;
    const pullOut = revised ? 36.6 : 35;
    attacker.lane = racingLine(attacker.distance) + side * passWidth * ease((time - pullOut) / 1.6);
    attacker.maneuver = time < pullOut ? 'tow' : time < pullOut + 1.6 ? 'pull-out' : 'alongside';
    defender.maneuver = 'defend';
    if (story.threeWay) {
      const third = cars.find(c => c.id === plan.closingOrder[2]);
      const thirdGap = revised ? Math.max(.42, story.gap + .26) : .42;
      third.distance = base - CAR_FRONT[third.id] - 16 + (16 - thirdGap) * ease(u);
      third.lane = racingLine(third.distance) - side * passWidth * ease((time - pullOut) / 1.6);
      third.maneuver = time < pullOut ? 'tow' : time < pullOut + 1.6 ? 'pull-out' : 'alongside';
    }
  }
  return cars;
}

function layout(round, plan, time) {
  if (time <= 40) return beforeFinish(round, plan, time);
  if (!plan.finish) {
    plan.finish = beforeFinish(round, plan, 40);
    const before = beforeFinish(round, plan, 39.999);
    plan.finish.forEach((car, index) => {
      car.endSpeed = (car.distance - before[index].distance) / .001;
      // Each car only starts coasting once its own nose reaches the line, so cars
      // that are still short of it hold full speed to the flag instead of lifting
      // off with the winner. The winner is already on the line, so it is unchanged.
      car.lineDelay = Math.max(0, (circuit.length - car.distance - CAR_FRONT[car.id]) / Math.max(1, car.endSpeed));
    });
  }
  const elapsed = time - 40;
  return plan.finish.map(car => {
    const rank = plan.finalOrder.indexOf(car.id), coast = 2 - rank * .27;
    const since = elapsed - car.lineDelay;
    if (since <= 0) return { id: car.id, distance: car.distance + car.endSpeed * elapsed, lane: car.lane, maneuver: 'follow' };
    const freeCoast = Math.min(since, 2.4);
    const u = clamp((since - 2.4) / 1.6);
    const distance = car.distance + car.endSpeed * car.lineDelay + car.endSpeed * coast * (1 - Math.exp(-freeCoast / coast))
      + car.endSpeed * Math.exp(-2.4 / coast) * 1.6 * (u - u * u + u ** 3 / 3);
    // A three-wide finish keeps its parallel paths during the short coast;
    // staggered line crossings can leave too little clearance for a merge.
    const lane = round.raceVersion >= 6 && plan.story.threeWay ? car.lane : car.lane * (1 - ease((since - 1.8) / 1.2));
    return { id: car.id, distance, lane, maneuver: 'follow' };
  });
}

function positioned(round, plan, time) {
  return layout(round, plan, Math.max(0, time)).map(car => ({ ...car, pose: circuit.pose(car.distance, car.lane) }));
}

export function sampleFlowRace(round, elapsed) {
  const time = Number.isFinite(elapsed) ? Math.max(0, elapsed) : 0;
  let plan = plans.get(round);
  if (!plan) { plan = planBattles(round); plans.set(round, plan); }
  const h = .002, cars = positioned(round, plan, time);
  const before = positioned(round, plan, Math.max(0, time - h)), after = positioned(round, plan, time + h);
  cars.forEach((car, i) => {
    const a = before[i].pose, b = after[i].pose, span = time < h ? time + h : 2 * h;
    const vx = (b.x - a.x) / span, vz = (b.z - a.z) / span;
    const ax = (b.x - 2 * car.pose.x + a.x) / (h * h), az = (b.z - 2 * car.pose.z + a.z) / (h * h);
    car.speed = time === 0 ? 0 : Math.hypot(vx, vz);
    car.acceleration = car.speed > .1 && time >= h ? (vx * ax + vz * az) / car.speed : 0;
    car.steering = car.speed > 1 ? Math.atan(2.7 * (vz * ax - vx * az) / car.speed ** 3) : 0;
    const trackHeading = car.pose.heading;
    if (car.speed > .1) car.pose.heading = Math.atan2(vx, vz);
    car.frontDistance = car.distance + CAR_FRONT[car.id] * Math.cos(car.pose.heading - trackHeading);
  });
  const order = [...cars].sort((a, b) => b.frontDistance - a.frontDistance || a.id.localeCompare(b.id)).map(c => c.id);
  cars.forEach(car => { car.rank = order.indexOf(car.id) + 1; });
  const lead = cars.find(c => c.id === order[0]);
  let action = plan.actions.find(a => time >= a.start && time < a.end);
  if (plan.story.finalDuel && time >= 35 && time < 40) action = { id: `${plan.story.id}:finish`, kind: plan.story.threeWay ? 'threeWay' : 'photoBattle',
    attacker: round.winnerId, defender: plan.closingOrder[0], start: 35, end: 40 };
  let event = action ? { id: action.id, kind: action.kind, carId: action.attacker,
    focusIds: [action.attacker, action.defender], start: action.start, end: action.end,
    completed: order.indexOf(action.attacker) < order.indexOf(action.defender) } : null;
  const phase = time >= 40 ? 'finished' : time >= 37 ? 'sprint' : lead.distance >= circuit.curveStart && lead.distance <= circuit.curveEnd ? 'corner' : time < 7 ? 'launch' : 'straight';
  if (event && phase === 'corner') event = { ...event, kind: 'cornerDuel' };
  else if (event && time >= 8 && time < 11) event = { ...event, kind: 'brakingAttack' };
  if (event && round.raceVersion >= 6) {
    const u = (time-action.start)/(action.end-action.start);
    let kind = event.kind;
    if (action.tactic === 'switchback') kind = u < .38 ? 'prepareExit' : u < .59 ? 'cutback' : 'exitAttack';
    else if (action.tactic === 's-duel') kind = u < .5 ? 'outsideSetup' : 'secondApexAttack';
    else if (action.tactic === 'inside-brake') kind = 'brakingAttack';
    else if (action.tactic === 'slipstream') kind = 'slipstreamAttack';
    else if (time >= 35 && time < 36.6) kind = 'followTow';
    event = { ...event, id: `${action.id}:${kind}`, kind };
  }
  return { cars, order, phase, time, duration: 40, storyId: plan.story.id, trackId: circuit.id, trackLength: circuit.length,
    event, focusIds: event?.focusIds ?? order.slice(0, 2), braking: time > 8 && time < 11 && lead.acceleration < -2,
    accelerating: time >= 18 && time < 23, leader: order[0], finished: time >= 40 };
}
